import React from "react";

const PrivacyPolicy = () => {
    // Styles matching the Terms and Conditions for consistency
    const styles = {
        container: {
            maxWidth: "900px",
            margin: "0 auto",
            padding: "40px 20px",
            fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
            lineHeight: "1.7",
            color: "#2c3e50",
        },
        header: {
            textAlign: "center",
            marginBottom: "40px",
            borderBottom: "2px solid #eee",
            paddingBottom: "20px",
        },
        h1: {
            fontSize: "2.5rem",
            marginBottom: "10px",
            color: "#1a1a1a",
        },
        h3: {
            fontSize: "1.4rem",
            marginTop: "35px",
            marginBottom: "15px",
            color: "#2c3e50",
            borderLeft: "4px solid #28a745", // Green accent for Privacy/Security
            paddingLeft: "15px",
        },
        section: {
            marginBottom: "20px",
        },
        list: {
            paddingLeft: "20px",
            marginBottom: "15px",
        },
        listItem: {
            marginBottom: "10px",
        },
        contactBox: {
            backgroundColor: "#f8f9fa",
            padding: "20px",
            borderRadius: "5px",
            marginTop: "20px",
            border: "1px solid #dee2e6",
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.h1}>Privacy Policy</h1>
                <p>Last Updated: {new Date().toLocaleDateString()}</p>
            </div>

            <div style={styles.section}>
                <p>
                    At <strong>Kanchans Choice</strong>, we value your trust. This Privacy Policy outlines how we collect, use, and protect your personal information when you visit our website and purchase our products. We are committed to transparency and ensuring your data remains secure.
                </p>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>1. Information We Collect</h3>
                <p>We collect information to provide a better shopping experience and fulfill your orders. This includes:</p>
                <ul style={styles.list}>
                    <li style={styles.listItem}><strong>Personal Information:</strong> When you make a purchase or create an account, we collect your name, billing address, shipping address, email address, and phone number.</li>
                    <li style={styles.listItem}><strong>Payment Information:</strong> We do <strong>not</strong> store your credit card details on our servers. All payment transactions are processed through secure third-party payment gateways (such as Stripe, PayPal, or Square) which encrypt your data.</li>
                    <li style={styles.listItem}><strong>Technical Data:</strong> We automatically collect information about your device, including your IP address, browser type, and operating system, to help us optimize our website for your screen.</li>
                </ul>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>2. How We Use Your Information</h3>
                <p>We use the information we collect for the following purposes:</p>
                <ul style={styles.list}>
                    <li style={styles.listItem}><strong>Order Fulfillment:</strong> To process your payment, arrange for shipping, and provide you with invoices and order confirmations.</li>
                    <li style={styles.listItem}><strong>Customer Support:</strong> To communicate with you regarding your order, returns, or questions.</li>
                    <li style={styles.listItem}><strong>Security:</strong> To screen our orders for potential risk or fraud.</li>
                    <li style={styles.listItem}><strong>Improvement:</strong> To analyze trends and improve the design and functionality of our website.</li>
                </ul>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>3. Sharing Your Information</h3>
                <p>
                    <strong>We do not sell your personal data.</strong> However, we share your information with trusted third parties solely to operate our business:
                </p>
                <ul style={styles.list}>
                    <li style={styles.listItem}><strong>Service Providers:</strong> We share data with shipping carriers (e.g., USPS, UPS) to deliver your package and payment processors to handle transactions.</li>
                    <li style={styles.listItem}><strong>Legal Compliance:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or a government agency).</li>
                </ul>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>4. Cookies and Tracking</h3>
                <p>
                    Our website uses "cookies" (small data files stored on your device) to enhance your experience. We use cookies to:
                </p>
                <ul style={styles.list}>
                    <li style={styles.listItem}>Keep track of items in your shopping cart.</li>
                    <li style={styles.listItem}>Remember your preferences for future visits.</li>
                    <li style={styles.listItem}>Analyze site traffic to understand which products are popular.</li>
                </ul>
                <p>
                    You can choose to disable cookies through your browser settings, though this may affect your ability to use certain features of our site (like the checkout process).
                </p>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>5. Data Security</h3>
                <p>
                    We implement a variety of security measures to maintain the safety of your personal information. Your personal information is contained behind secured networks and is only accessible by a limited number of persons who have special access rights to such systems and are required to keep the information confidential. In addition, all sensitive/credit information you supply is encrypted via Secure Socket Layer (SSL) technology.
                </p>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>6. Your Rights (Virginia Residents)</h3>
                <p>
                    While we are a growing business, we respect the spirit of the Virginia Consumer Data Protection Act (VCDPA). You have the right to:
                </p>
                <ul style={styles.list}>
                    <li style={styles.listItem}><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
                    <li style={styles.listItem}><strong>Correction:</strong> Request that we correct inaccurate data.</li>
                    <li style={styles.listItem}><strong>Deletion:</strong> Request that we delete your personal data (subject to our need to keep records for tax and legal purposes).</li>
                </ul>
                <p>To exercise these rights, please contact us at the email or phone number listed below.</p>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>7. Children's Privacy</h3>
                <p>
                    Our website is not intended for individuals under the age of 13. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us to have it removed.
                </p>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>8. Changes to This Policy</h3>
                <p>
                    We may update this privacy policy from time to time in order to reflect, for example, changes to our practices or for other operational, legal, or regulatory reasons. Updates will be posted on this page with the "Last Updated" date at the top.
                </p>
            </div>

            <div style={styles.section}>
                <h3 style={styles.h3}>9. Contact Us</h3>
                <p>
                    For more information about our privacy practices, if you have questions, or if you would like to make a complaint, please contact us:
                </p>
                <div style={styles.contactBox}>
                    <strong>Kanchans Choice Support</strong><br /><br />
                    📧 Email: support@kanchanschoice.com<br />
                    📞 Phone: 571-699-6505<br />
                    📍 Location: Leesburg, Virginia, USA
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;